/**
 * ColumnEngine - Stap-voor-stap kolomsgewijs rekenen
 *
 * Ondersteunt:
 * - Optellen met overhouden
 * - Aftrekken met lenen
 * - Vermenigvuldigen
 * - Delen
 */

export type ColumnOperation = 'add' | 'subtract' | 'multiply' | 'divide'

export interface ColumnStep {
  stepNumber: number
  column: number // Van rechts naar links (0 = eenheden, 1 = tientallen, etc.)
  operation: string // Beschrijving van de stap
  calculation: string // De berekening (bijv. "7 + 5 = 12")
  result: number
  carry?: number // Overhouden bij optellen
  borrow?: number // Lenen bij aftrekken
  explanation: string // Kindvriendelijke uitleg
}

export interface ColumnCalculation {
  operation: ColumnOperation
  operand1: number
  operand2: number
  result: number
  steps: ColumnStep[]
  visualGrid: ColumnGrid
}

export interface ColumnGrid {
  rows: ColumnRow[]
  columns: number // Aantal kolommen (cijfers)
}

export interface ColumnRow {
  type:
    | 'operand1'
    | 'operand2'
    | 'operation'
    | 'line'
    | 'result'
    | 'carry'
    | 'borrow'
  values: (number | string | null)[]
  label?: string
}

/**
 * Splits een getal in cijfers (van rechts naar links)
 */
function splitDigits(num: number): number[] {
  return String(num).split('').map(Number).reverse()
}

/**
 * Voeg cijfers samen tot getal
 */
function joinDigits(digits: number[]): number {
  return parseInt(digits.reverse().join(''))
}

/**
 * OPTELLEN met overhouden
 */
export function calculateAddition(a: number, b: number): ColumnCalculation {
  const digitsA = splitDigits(a)
  const digitsB = splitDigits(b)
  const maxLength = Math.max(digitsA.length, digitsB.length)

  const steps: ColumnStep[] = []
  const resultDigits: number[] = []
  let carry = 0

  for (let col = 0; col < maxLength; col++) {
    const digitA = digitsA[col] || 0
    const digitB = digitsB[col] || 0
    const sum = digitA + digitB + carry
    const digit = sum % 10
    const newCarry = Math.floor(sum / 10)

    resultDigits.push(digit)

    // Maak stap
    const columnName = getColumnName(col)
    let explanation = ''
    let calculation = ''

    if (carry > 0) {
      calculation = `${digitA} + ${digitB} + ${carry} = ${sum}`
      explanation = `Bij de ${columnName}: ${digitA} + ${digitB} + ${carry} (overhouden) = ${sum}. `
    } else {
      calculation = `${digitA} + ${digitB} = ${sum}`
      explanation = `Bij de ${columnName}: ${digitA} + ${digitB} = ${sum}. `
    }

    if (newCarry > 0) {
      explanation += `Dat is ${digit}, en ${newCarry} onthouden.`
    } else {
      explanation += `Dat is ${digit}.`
    }

    steps.push({
      stepNumber: col + 1,
      column: col,
      operation: 'optellen',
      calculation,
      result: digit,
      carry: newCarry > 0 ? newCarry : undefined,
      explanation,
    })

    carry = newCarry
  }

  // Als er nog carry over is, voeg toe
  if (carry > 0) {
    resultDigits.push(carry)
  }

  const result = joinDigits([...resultDigits])

  // Maak visual grid
  const visualGrid = createAdditionGrid(a, b, result, steps)

  return {
    operation: 'add',
    operand1: a,
    operand2: b,
    result,
    steps,
    visualGrid,
  }
}

/**
 * AFTREKKEN met lenen
 */
export function calculateSubtraction(a: number, b: number): ColumnCalculation {
  if (a < b) {
    throw new Error('Kan niet aftrekken: resultaat zou negatief zijn')
  }

  const digitsA = [...splitDigits(a)]
  const digitsB = splitDigits(b)
  const maxLength = Math.max(digitsA.length, digitsB.length)

  const steps: ColumnStep[] = []
  const resultDigits: number[] = []

  for (let col = 0; col < maxLength; col++) {
    let digitA = digitsA[col] || 0
    const digitB = digitsB[col] || 0

    // Check of we moeten lenen
    let borrowed = 0
    if (digitA < digitB) {
      // Zoek de volgende kolom om van te lenen
      let borrowCol = col + 1
      while (borrowCol < digitsA.length && digitsA[borrowCol] === 0) {
        digitsA[borrowCol] = 9
        borrowCol++
      }

      if (borrowCol < digitsA.length) {
        digitsA[borrowCol]--
        digitA += 10
        borrowed = 1
      }
    }

    const diff = digitA - digitB
    resultDigits.push(diff)

    // Maak stap
    const columnName = getColumnName(col)
    let explanation = ''
    let calculation = ''

    if (borrowed > 0) {
      calculation = `${digitA} - ${digitB} = ${diff}`
      explanation = `Bij de ${columnName}: ${digitA - 10} is te klein, dus we lenen 1. Nu is het ${digitA}. ${digitA} - ${digitB} = ${diff}.`
    } else {
      calculation = `${digitA} - ${digitB} = ${diff}`
      explanation = `Bij de ${columnName}: ${digitA} - ${digitB} = ${diff}.`
    }

    steps.push({
      stepNumber: col + 1,
      column: col,
      operation: 'aftrekken',
      calculation,
      result: diff,
      borrow: borrowed > 0 ? borrowed : undefined,
      explanation,
    })
  }

  const result = joinDigits([...resultDigits])

  // Maak visual grid
  const visualGrid = createSubtractionGrid(a, b, result, steps)

  return {
    operation: 'subtract',
    operand1: a,
    operand2: b,
    result,
    steps,
    visualGrid,
  }
}

/**
 * Helper: Kolomnaam (eenheden, tientallen, etc.)
 */
function getColumnName(col: number): string {
  const names = [
    'eenheden',
    'tientallen',
    'honderdtallen',
    'duizendtallen',
    'tienduizendtallen',
  ]
  return names[col] || `kolom ${col + 1}`
}

/**
 * Maak visual grid voor optellen
 */
function createAdditionGrid(
  a: number,
  b: number,
  result: number,
  steps: ColumnStep[],
): ColumnGrid {
  const digitsA = splitDigits(a).reverse()
  const digitsB = splitDigits(b).reverse()
  const digitsResult = splitDigits(result).reverse()
  const maxLength = Math.max(
    digitsA.length,
    digitsB.length,
    digitsResult.length,
  )

  // Pad arrays
  while (digitsA.length < maxLength) digitsA.unshift(null as any)
  while (digitsB.length < maxLength) digitsB.unshift(null as any)
  while (digitsResult.length < maxLength) digitsResult.unshift(null as any)

  // Carry row
  const carryValues = new Array(maxLength).fill(null)
  steps.forEach((step) => {
    if (step.carry) {
      const index = maxLength - step.column - 2
      if (index >= 0) carryValues[index] = step.carry
    }
  })

  const rows: ColumnRow[] = [
    { type: 'carry', values: carryValues, label: 'onthouden' },
    { type: 'operand1', values: digitsA },
    { type: 'operand2', values: digitsB, label: '+' },
    { type: 'line', values: new Array(maxLength).fill('─') },
    { type: 'result', values: digitsResult },
  ]

  return { rows, columns: maxLength }
}

/**
 * Maak visual grid voor aftrekken
 */
function createSubtractionGrid(
  a: number,
  b: number,
  result: number,
  steps: ColumnStep[],
): ColumnGrid {
  const digitsA = splitDigits(a).reverse()
  const digitsB = splitDigits(b).reverse()
  const digitsResult = splitDigits(result).reverse()
  const maxLength = Math.max(
    digitsA.length,
    digitsB.length,
    digitsResult.length,
  )

  // Pad arrays
  while (digitsA.length < maxLength) digitsA.unshift(null as any)
  while (digitsB.length < maxLength) digitsB.unshift(null as any)
  while (digitsResult.length < maxLength) digitsResult.unshift(null as any)

  // Borrow indicators
  const borrowValues = new Array(maxLength).fill(null)
  steps.forEach((step) => {
    if (step.borrow) {
      const index = maxLength - step.column - 1
      if (index >= 0) borrowValues[index] = '↓'
    }
  })

  const rows: ColumnRow[] = [
    { type: 'borrow', values: borrowValues, label: 'lenen' },
    { type: 'operand1', values: digitsA },
    { type: 'operand2', values: digitsB, label: '−' },
    { type: 'line', values: new Array(maxLength).fill('─') },
    { type: 'result', values: digitsResult },
  ]

  return { rows, columns: maxLength }
}

/**
 * Genereer kindvriendelijke uitleg voor hele berekening
 */
export function explainCalculation(calc: ColumnCalculation): string {
  const { operation, operand1, operand2, result } = calc

  let intro = ''
  switch (operation) {
    case 'add':
      intro = `We gaan ${operand1} + ${operand2} uitrekenen. We beginnen rechts bij de eenheden en werken naar links.`
      break
    case 'subtract':
      intro = `We gaan ${operand1} − ${operand2} uitrekenen. We beginnen rechts bij de eenheden en werken naar links.`
      break
    default:
      intro = `We gaan ${operand1} en ${operand2} met elkaar rekenen.`
  }

  const stepExplanations = calc.steps
    .map((step, i) => `Stap ${i + 1}: ${step.explanation}`)
    .join('\n\n')

  const conclusion = `Het antwoord is ${result}.`

  return `${intro}\n\n${stepExplanations}\n\n${conclusion}`
}
